// Centralized cache policy and utilities

export type CacheLayer = 'fetch' | 'axios' | 'react-query' | 'any'
export type Storage = 'memory' | 'sessionStorage' | 'localStorage'

export type Rule = {
  pattern: RegExp | string
  methods?: string[]
  enabled?: boolean
  ttlMinutes?: number
  storage?: Storage
  layer?: CacheLayer
}

export type Fallback = {
  enabled: boolean
  ttlMinutes: number
  storage: Storage
}

export type EffectivePolicy = {
  enabled: boolean
  ttlMinutes: number
  storage: Storage
}

function toRegExp(pattern: RegExp | string): RegExp {
  if (pattern instanceof RegExp) return pattern
  // simple wildcard -> regex conversion
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')
  return new RegExp(`^${escaped}$`)
}

function methodMatches(methods: string[] | undefined, method: string): boolean {
  if (!methods || methods.length === 0) return method.toUpperCase() === 'GET'
  return methods.map((m) => m.toUpperCase()).includes(method.toUpperCase())
}

export function isCacheEnabled(): boolean {
  // Server-side: always return false (caching only works on client)
  if (typeof window === 'undefined') {
    return false
  }
  
  // Client-side checks
  const nodeEnv = process.env.NODE_ENV
  const enableFlag = process.env.NEXT_PUBLIC_ENABLE_API_CACHE
  
  // Debug log on first call (client-side only)
  if (!(window as any).__cacheDebugLogged) {
    (window as any).__cacheDebugLogged = true
    // eslint-disable-next-line no-console
    console.log('[CACHE] isCacheEnabled() check:', {
      nodeEnv,
      enableFlag,
      enableFlagType: typeof enableFlag,
      isTest: nodeEnv === 'test',
      willEnable: nodeEnv !== 'test' && enableFlag === 'true',
      allEnvVars: {
        NODE_ENV: process.env.NODE_ENV,
        NEXT_PUBLIC_ENABLE_API_CACHE: process.env.NEXT_PUBLIC_ENABLE_API_CACHE,
        NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL
      }
    })
  }
  
  // Disable if in test mode
  if (nodeEnv === 'test') return false
  
  // Default to false unless explicitly enabled
  if (enableFlag === 'true') return true
  
  return false
}

const fallback: Fallback = {
  enabled: true,  // ✅ Default cache all GET requests for 20 minutes
  ttlMinutes: 20,
  storage: 'sessionStorage',
}

// Initial URL rules
const rules: Rule[] = [
  // NEVER cache polling/status endpoints (need real-time data)
  { pattern: /\/api\/v1\/assets\/[^/]+\/parsing-status/, methods: ['GET'], enabled: false, ttlMinutes: 0, storage: 'memory', layer: 'any' },

  // Credentials/profile
  { pattern: '/api/v1/users/me*', methods: ['GET'], enabled: true, ttlMinutes: 5, storage: 'sessionStorage', layer: 'any' },

  // Account Receivables - Dashboard
  { pattern: /\/api\/v1\/account-receivables\/dashboard/, methods: ['GET'], enabled: true, ttlMinutes: 10, storage: 'sessionStorage', layer: 'any' },
  
  // Account Receivables - Yardi Tenant Charges (all endpoints)
  { pattern: /\/api\/v1\/yardi-tenant-charges/, methods: ['GET'], enabled: true, ttlMinutes: 5, storage: 'sessionStorage', layer: 'any' },

  // Reference data
  { pattern: /\/api\/.*\/(gl-codes|properties)/, methods: ['GET'], enabled: true, ttlMinutes: 120, storage: 'localStorage', layer: 'any' },
]

export function getCachePolicy(url: string, method: string = 'GET', layer: CacheLayer = 'any'): EffectivePolicy {
  // Disabled globally for tests/dev flag
  if (!isCacheEnabled()) {
    return { enabled: false, ttlMinutes: 0, storage: 'memory' }
  }

  // Find first matching rule by URL, method, and layer compatibility
  for (const rule of rules) {
    const regex = toRegExp(rule.pattern)
    if (!regex.test(url)) continue
    if (!methodMatches(rule.methods, method)) continue
    if (rule.layer && rule.layer !== 'any' && rule.layer !== layer) continue

    return {
      enabled: rule.enabled ?? false,
      ttlMinutes: rule.ttlMinutes ?? fallback.ttlMinutes,
      storage: rule.storage ?? fallback.storage,
    }
  }

  // Fallback policy (permissive; URL rules can override)
  return {
    enabled: fallback.enabled,
    ttlMinutes: fallback.ttlMinutes,
    storage: fallback.storage,
  }
}

// Debug resolver to expose which rule matched (for logging)
export function resolvePolicyDebug(
  url: string,
  method: string = 'GET',
  layer: CacheLayer = 'any'
): { policy: EffectivePolicy; matchedPattern?: string; globallyEnabled: boolean } {
  const globallyEnabled = isCacheEnabled()

  // Find first matching rule by URL, method, and layer compatibility
  for (const rule of rules) {
    const regex = toRegExp(rule.pattern)
    if (!regex.test(url)) continue
    if (!methodMatches(rule.methods, method)) continue
    if (rule.layer && rule.layer !== 'any' && rule.layer !== layer) continue

    const policy: EffectivePolicy = globallyEnabled
      ? {
          enabled: rule.enabled ?? false,
          ttlMinutes: rule.ttlMinutes ?? fallback.ttlMinutes,
          storage: rule.storage ?? fallback.storage,
        }
      : { enabled: false, ttlMinutes: 0, storage: 'memory' }

    return { policy, matchedPattern: typeof rule.pattern === 'string' ? rule.pattern : rule.pattern.toString(), globallyEnabled }
  }

  const policy: EffectivePolicy = globallyEnabled
    ? { enabled: fallback.enabled, ttlMinutes: fallback.ttlMinutes, storage: fallback.storage }
    : { enabled: false, ttlMinutes: 0, storage: 'memory' }

  return { policy, matchedPattern: undefined, globallyEnabled }
}

// Helpers for React Query defaults derived from the fallback
export function getReactQueryDefaults(): { enabled: boolean; ttlMinutes: number } {
  if (!isCacheEnabled()) return { enabled: false, ttlMinutes: 0 }
  return { enabled: fallback.enabled, ttlMinutes: fallback.ttlMinutes }
}

// Utility to merge per-call overrides on top of policy
export function mergePolicyWithOverrides(policy: EffectivePolicy, overrides?: Partial<EffectivePolicy>): EffectivePolicy {
  if (!overrides) return policy
  return {
    enabled: overrides.enabled ?? policy.enabled,
    ttlMinutes: overrides.ttlMinutes ?? policy.ttlMinutes,
    storage: overrides.storage ?? policy.storage,
  }
}


